/**
 * Utility function to migrate development documents from temp to permanent storage
 * Run this from the browser console while logged in as admin
 */

import { collection, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { ref, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage';
import { db, storage } from '../services/firebase/config';

export async function listTempDocuments() {
  console.log('🔍 Checking for development documents in temp folder...\n');

  try {
    const developmentsSnapshot = await getDocs(collection(db, 'developments'));
    console.log(`Found ${developmentsSnapshot.size} developments\n`);

    let tempDocCount = 0;
    const affectedDevelopments: any[] = []; // intentional: Firestore document data with dynamic shape

    for (const docSnapshot of developmentsSnapshot.docs) {
      const development = docSnapshot.data();
      const developmentId = docSnapshot.id;

      const tempDocs: string[] = [];

      // Check all document fields
      if (development.documents) {
        Object.entries(development.documents).forEach(([key, value]) => {
          if (typeof value === 'string' && value.includes('/temp/')) {
            tempDocs.push(`documents.${key}: ${value.substring(0, 80)}...`);
            tempDocCount++;
          }
        });
      }

      if (tempDocs.length > 0) {
        affectedDevelopments.push({
          id: developmentId,
          name: development.name,
          tempDocs
        });
      }
    }

    console.log('='.repeat(60));
    console.log('📊 SUMMARY:');
    console.log('='.repeat(60));
    console.log(`Total developments checked: ${developmentsSnapshot.size}`);
    console.log(`Developments with temp documents: ${affectedDevelopments.length}`);
    console.log(`Total temp documents found: ${tempDocCount}\n`);

    if (affectedDevelopments.length > 0) {
      console.log('🚨 AFFECTED DEVELOPMENTS:');
      console.log('-'.repeat(60));

      affectedDevelopments.forEach(dev => {
        console.log(`\n📦 ${dev.name} (ID: ${dev.id})`);
        dev.tempDocs.forEach((doc: string) => {
          console.log(`   - ${doc}`);
        });
      });

      console.log('\n\nTo migrate these documents, run: window.migrateTempDocuments()');
    } else {
      console.log('✅ No documents found in temp folder!');
    }

    return affectedDevelopments;
  } catch (error) {
    console.error('❌ Error:', error);
    throw error;
  }
}

export async function migrateTempDocuments(dryRun = false) {
  console.log(`\n🚀 ${dryRun ? 'DRY RUN - ' : ''}Starting migration of temp documents...\n`);

  try {
    const developmentsSnapshot = await getDocs(collection(db, 'developments'));

    let totalMigrated = 0;
    let totalErrors = 0;
    const migratedDevelopments: string[] = [];

    for (const docSnapshot of developmentsSnapshot.docs) {
      const development = docSnapshot.data();
      const developmentId = docSnapshot.id;

      const updates: any = {};
      let hasTemp = false;

      // Check and migrate documents
      if (development.documents) {
        for (const [key, value] of Object.entries(development.documents)) {
          if (typeof value === 'string' && value.includes('/temp/')) {
            hasTemp = true;
            console.log(`\n📦 Processing: ${development.name} - ${key}`);

            if (dryRun) {
              console.log(`   [DRY RUN] Would migrate: ${value.substring(0, 80)}...`);
              totalMigrated++;
              continue;
            }

            try {
              // Extract file path from URL
              const urlParts = value.split('/o/');
              if (urlParts.length > 1) {
                const encodedPath = urlParts[1].split('?')[0];
                const filePath = decodeURIComponent(encodedPath);

                if (filePath.startsWith('temp/')) {
                  // Download the file
                  console.log(`   Downloading from temp...`);
                  const response = await fetch(value);

                  if (!response.ok) {
                    throw new Error(`Failed to download: ${response.status}`);
                  }

                  const blob = await response.blob();

                  // Generate new path
                  const fileName = filePath.split('/').pop() || 'document.pdf';
                  const timestamp = Date.now();
                  const newPath = `developments/${developmentId}/${key}/${timestamp}_${fileName}`;

                  console.log(`   Uploading to: ${newPath}`);

                  // Upload to new location
                  const newRef = ref(storage, newPath);
                  const snapshot = await uploadBytes(newRef, blob);
                  const newUrl = await getDownloadURL(snapshot.ref);

                  // Store update
                  updates[`documents.${key}`] = newUrl;

                  console.log(`   ✅ Successfully migrated`);
                  totalMigrated++;
                }
              }
            } catch (error: unknown) {
              console.error(`   ❌ Error: ${(error instanceof Error ? error.message : String(error))}`);
              totalErrors++;
            }
          }
        }
      }

      // Update the development document if there are changes
      if (!dryRun && Object.keys(updates).length > 0) {
        updates['lastMigrated'] = Timestamp.now();
        await updateDoc(doc(db, 'developments', developmentId), updates);
        console.log(`✅ Updated ${development.name} with ${Object.keys(updates).length - 1} new URLs`);
        migratedDevelopments.push(development.name);
      }
    }

    console.log('\n' + '='.repeat(60));
    console.log(`📊 Migration ${dryRun ? 'DRY RUN ' : ''}Summary:`);
    console.log('='.repeat(60));
    console.log(`✅ Documents ${dryRun ? 'would be ' : ''}migrated: ${totalMigrated}`);
    console.log(`❌ Errors: ${totalErrors}`);

    if (migratedDevelopments.length > 0) {
      console.log(`\n📋 ${dryRun ? 'Would update' : 'Updated'} developments:`);
      migratedDevelopments.forEach(name => console.log(`   - ${name}`));
    }

    if (dryRun && totalMigrated > 0) {
      console.log('\n⚠️  This was a DRY RUN. To actually migrate, run:');
      console.log('window.migrateTempDocuments(false)');
    }

    return { totalMigrated, totalErrors, migratedDevelopments };
  } catch (error) {
    console.error('❌ Fatal error:', error);
    throw error;
  }
}

// Make functions available globally for browser console
if (typeof window !== 'undefined') {
  (window as any).listTempDocuments = listTempDocuments;
  (window as any).migrateTempDocuments = migrateTempDocuments;
}